<!-- Component can be used in microscope and periscope -->
<template>
  <!-- A filter that is on or off: a row of these makes a filter group, several of which may be on. -->
  <button type="button" class="filter-chip" :aria-pressed="isOn ? 'true' : 'false'" @click="toggle">
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    // For shared components to work in both vue 2.7 and vue 3 we support both `value` and `modelValue`.
    value?: boolean
    modelValue?: boolean
  }>(),
  {
    // Keep these `undefined` defaults: without them Vue's boolean casting turns an absent `modelValue` into
    // `false`, and `modelValue ?? value` below then ignores Vue 2's v-model (`value`).
    value: undefined,
    modelValue: undefined,
  },
)

const emit = defineEmits<{
  (e: 'input', v: boolean): void
  (e: 'update:modelValue', v: boolean): void
}>()

const isOn = computed(() => props.modelValue ?? props.value ?? false)

const toggle = () => {
  emit('input', !isOn.value)
  emit('update:modelValue', !isOn.value)
}
</script>

<style lang="scss" scoped>
// Off: outlined on the canvas. On: the soft brand style AppChip uses for `color="brand"`.
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-12);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-default);
  color: var(--color-fg-default);
  font: inherit;
  cursor: pointer;

  &:hover {
    background-color: var(--color-canvas-hover);
    border-color: var(--color-border-hover);
  }

  &[aria-pressed='true'] {
    background-color: var(--color-brand-subtle);
    border-color: var(--color-brand-default);
    color: var(--color-brand-on-subtle);
  }

  &:focus-visible {
    outline: 2px solid var(--color-brand-default);
    outline-offset: 1px;
  }
}
</style>
